import { useMemo } from 'react';
import { useUtils } from '~/shared/hooks/useUtils';
import { useAppliedSpecialPricing } from './useAppliedSpecialPrice';
import { hasMetSpecialPriceRequirements, interpolateSalesforceText } from './utils';

export const useAppliedProductSpecialPrice = (product: {
    id: string;
    // We can ignore all other properties of the product apart from the above.
    [key: string]: unknown;
}) => {
    const { convertPriceToLocale } = useUtils();
    const { getProductPromotion } = useAppliedSpecialPricing();

    const promotion = useMemo(
        () => getProductPromotion(product.id),
        [getProductPromotion, product.id],
    );

    const {
        basketAmountThreshold = 0,
        basketNumberOfProductsThreshold = 0,
        isAmountThreshold = true,
        quantityInBasket = 0,
        amountInBasket = 0,
        splashAboveThreshold = '',
        splashBelowThreshold = '',
        staticTileFooterText = '',
        tileFooterText = '',
        discountAmount = 0,
    } = promotion || {};

    // Calculated threshold value until requirement is met.
    const dynamicThresholdValue = isAmountThreshold
        ? convertPriceToLocale(Math.max(basketAmountThreshold - amountInBasket, 0))
        : basketNumberOfProductsThreshold - quantityInBasket;

    const hasMetRequirements = useMemo(
        () => hasMetSpecialPriceRequirements(promotion),
        [promotion],
    );

    const dynamicCampaignText = useMemo(() => {
        const text = staticTileFooterText || tileFooterText || '';

        return interpolateSalesforceText(text, [dynamicThresholdValue ?? 0, discountAmount ?? 0]);
    }, [staticTileFooterText, tileFooterText, dynamicThresholdValue]);

    const dynamicSplashText = useMemo(() => {
        if (hasMetRequirements) return splashAboveThreshold;

        return interpolateSalesforceText(splashBelowThreshold, [
            dynamicThresholdValue ?? 0,
            discountAmount ?? 0,
        ]);
    }, [hasMetRequirements, splashAboveThreshold, splashBelowThreshold, dynamicThresholdValue]);

    return {
        ...promotion,
        isActive: !!promotion,
        dynamicSplashText,
        dynamicCampaignText,
        hasMetRequirements,
    };
};
